import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import debounce from 'lodash.debounce';
import './Platform.css';
import { useUser } from '../../context/UserContext';
import { useChatbotContext } from '../../context/ChatbotContext';
// import Header from './PlatformHeader/PlatformHeader'; // DEPRECATED
import Sidebar from './Sidebar/Sidebar';
import Content from './Content/Content';
import FeatureGuard from '../../components/FeatureGuard';
import Chatbot from './Chatbot/Chatbot';
import Voicebot from './Voicebot/Voicebot';
import ProSettings from './ProSettings/ProSettings';
import Inbox from './Inbox/Inbox';
import Dashboard from './Chatbot/ChatbotBuilder/Analytics/Dashboard/Dashboard';
import ImpersonationPage from './Admin/ImpersonationPage';
import logger from '../../utils/logger';
import GlobalHandoffListener from './GlobalHandoffListener';
import { Toaster } from 'sonner';

const defaultConfig = {
  assistantName: 'Bella',
  messagePrompt: "Type your message and hit 'Enter'",
  introMessageText: "Hello! How can I help you today?",
  botTagline: "Your friendly AI assistant",
  headerColor: '#745F7C',
  chatBackgroundColor: '#FFFFFF',
  botMessageBoxColor: '#E6DEED',
  botMessageTextColor: '#333333',
  userMessageBoxColor: '#B092AD',
  userMessageTextColor: '#ffffff',
  openAIVectorStoreID: '',
  openAIAssistantID: '',
  uploadedFiles: [],
  chatbotRolePrompt: 'You are a helpful Customer Support Executive...',
  emailChatTranscriptsTo: 'marketing@company.com',
  customIntroMessage: false,
  defaultLanguage: 'en',
  responseDelay: 0,
  openChatOnLoad: false,
  chatOpenDelay: 0,
  showPoweredBy: true,
  chatbotPosition: 'right',
  announcement: {
    text: '',
    enabled: false,
    style: 'info' // 'info' | 'warning' | 'success'
  },
};

const routeNameMap = {
  'Website': 'knowledge/website',
  'Files': 'knowledge/files',
  'OpenAI': 'knowledge/openai',
  'Identity': 'design/identity',
  'Theme': 'design/theme',
  'Toggle Button': 'design/toggle-button',
  'General': 'behavior/general',
  'Chat Menu': 'behavior/chat-menu',
  'ProactiveBubble': 'behavior/proactive',
  'HumanHandoff': 'behavior/human-handoff',
  'Forms': 'behavior/forms',
  'Walkthroughs': 'behavior/walkthroughs',
  'Connect': 'grow/connect',
  'Publish': 'grow/publish',
  /* Internal Builder Specific Overrides */
  'Profile': 'profile',
  'Advance': 'advance',
  'Integrations': 'integrations',
  'Integration': 'integration',
  'Dashboard': 'analytics',
  'Analytics': 'analytics',
  'Inbox': 'inbox'
};

const subTabMap = Object.entries(routeNameMap).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

function Platform() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname.toLowerCase();

  // 1. Derive state exactly from the URL configuration
  let activeTab = 'Chatbot';
  let selectedChatbotId = null;
  let activeSubTab = null;
  let targetThreadId = null;

  if (path.startsWith('/overview')) {
    activeTab = 'Chatbot';
  } else if (path.startsWith('/inbox')) {
    activeTab = 'Inbox';
    const parts = path.split('/').filter(Boolean);
    if (parts[1] === 'thread' && parts[2]) {
      targetThreadId = parts[2];
    }
  } else if (path.startsWith('/pro-settings')) {
    activeTab = 'Pro+';
  } else if (path.startsWith('/analytics')) {
    activeTab = 'Analytics';
  } else if (path.startsWith('/admin/impersonation')) {
    activeTab = 'AdminImpersonation';
  } else if (path.startsWith('/chatbot/')) {
    const parts = path.split('/').filter(Boolean);
    if (parts.length >= 2) selectedChatbotId = parts[1];

    if (parts.length >= 5 && parts[2] === 'inbox' && parts[3] === 'thread') {
      // Format: /chatbot/:id/inbox/thread/:threadId
      activeTab = 'Inbox';
      activeSubTab = null;
      targetThreadId = parts[4];
    } else if (parts.length >= 4) {
      // Format: /chatbot/:id/category/subitem
      const category = parts[2];
      const routeSubTab = `${category}/${parts[3]}`; // match our reversed map shape
      activeSubTab = subTabMap[routeSubTab] || 'Website';
      activeTab = `Chatbot/${activeSubTab}`;
    } else if (parts.length === 3) {
      const routeSubTab = parts[2];

      if (routeSubTab === 'inbox') {
        activeTab = 'Inbox';
        activeSubTab = null;
      } else {
        activeSubTab = subTabMap[routeSubTab] || 'Website';
        activeTab = `Chatbot/${activeSubTab}`;
      }
    } else {
      activeSubTab = 'Website';
      activeTab = 'Chatbot/Website';
    }
  }

  // Clean up legacy URL parameters natively gracefully upgrading them to nested routes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('tab') || searchParams.has('thread')) {
      const oldTab = searchParams.get('tab');
      const oldThreadId = searchParams.get('thread');

      // Perform the transition seamlessly without leaving a history trail
      if (oldTab === 'Inbox' && oldThreadId) {
        navigate(`/inbox/thread/${oldThreadId}`, { replace: true });
      } else if (oldTab) {
        // Use our proxy function to figure out the right new URL route
        if (oldTab === 'Inbox') {
          const chatbotIdParam = localStorage.getItem('chatbotId');
          if (chatbotIdParam) navigate(`/chatbot/${chatbotIdParam}/inbox`, { replace: true });
          else navigate('/inbox', { replace: true });
        }
        else if (oldTab === 'Pro+') navigate('/pro-settings', { replace: true });
        else if (oldTab === 'Chatbot') navigate('/overview', { replace: true });
        else if (oldTab.startsWith('Chatbot/')) {
          const remaining = oldTab.substring(8);
          // Special case mapping fallback
          if (remaining === 'Dashboard' || remaining === 'Analytics') {
            const chatbotIdParam = localStorage.getItem('chatbotId');
            if (chatbotIdParam) navigate(`/chatbot/${chatbotIdParam}/analytics`, { replace: true });
            else navigate('/analytics', { replace: true });
          } else {
            const urlPart = routeNameMap[remaining] || remaining.toLowerCase();
            const chatbotIdParam = localStorage.getItem('chatbotId');
            if (chatbotIdParam) navigate(`/chatbot/${chatbotIdParam}/${urlPart}`, { replace: true });
            else navigate('/overview', { replace: true });
          }
        }
      }
    }
  }, [location.search, navigate]);

  // Auto-redirect raw chatbot URLs to their specific Inbox
  useEffect(() => {
    if (path.startsWith('/chatbot/')) {
      const parts = path.split('/').filter(Boolean);
      if (parts.length === 2 && parts[1]) {
        navigate(`/chatbot/${parts[1]}/inbox`, { replace: true });
      }
    }
  }, [path, navigate]);

  const [configState, setConfigState] = useState(() => {
    const savedConfig = localStorage.getItem('chatbotConfig');
    return savedConfig ? JSON.parse(savedConfig) : defaultConfig;
  });

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { user: currentUser, hasFeatureAccess, FEATURES, loading } = useUser();
  const { chatbots } = useChatbotContext();

  // 2. Backward compatibility proxy writers that issue route changes
  const handleTabChange = React.useCallback((tabName) => {
    if (tabName === 'Inbox') {
      if (selectedChatbotId) navigate(`/chatbot/${selectedChatbotId}/inbox`);
      else navigate('/inbox');
    }
    else if (tabName === 'Dashboard' || tabName === 'Analytics') {
      if (selectedChatbotId) navigate(`/chatbot/${selectedChatbotId}/analytics`);
      else navigate('/analytics');
    }
    else if (tabName === 'AdminImpersonation') navigate('/admin/impersonation');
    else if (tabName === 'Pro+') navigate('/pro-settings');
    else if (tabName === 'Chatbot') navigate('/overview');
    else if (tabName.startsWith('Chatbot/')) {
      const remaining = tabName.substring(8);
      const urlPart = routeNameMap[remaining] || remaining.toLowerCase();
      if (selectedChatbotId) {
        navigate(`/chatbot/${selectedChatbotId}/${urlPart}`);
      } else {
        navigate('/overview');
      }
    }
  }, [navigate, selectedChatbotId]);

  const setSelectedChatbotIdProxy = (id) => {
    if (id) navigate(`/chatbot/${id}/design/identity`);
    else navigate('/overview');
  };

  const setActiveSubTabProxy = (subTab) => {
    if (selectedChatbotId && subTab) {
      handleTabChange(`Chatbot/${subTab}`);
    }
  };

  const handleNotificationClick = (chatbotId, threadId) => {
    // Global handoff logic: auto navigates precisely to the deep link
    navigate(`/inbox/thread/${threadId}`);
  };

  const handleMobileMenuToggle = (isOpen) => {
    setIsMobileMenuOpen(isOpen);
  };
  const resetToDefaultConfig = () => {
    setConfigState(defaultConfig);
  };

  const saveConfigDebounced = debounce((config) => {
    localStorage.setItem('chatbotConfig', JSON.stringify(config));
  }, 500);

  useEffect(() => {
    saveConfigDebounced(configState);
  }, [configState, saveConfigDebounced]);

  const updateConfig = async (key, value) => {
    const newConfig = {
      ...configState,
      [key]: value,
    };
    setConfigState(newConfig);

    // Save to database
    try {
      // Get the Firebase token for authentication
      const token = currentUser ? await currentUser.getIdToken() : null;

      const headers = {
        'Content-Type': 'application/json',
      };

      // Add Authorization header if token is available
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          chatbotId: selectedChatbotId,
          config: newConfig,
          userId: currentUser?.uid
        }),
      });

      if (!response.ok) {
        logger.error('Failed to save configuration:', await response.text());
      }
    } catch (error) {
      logger.error('Error saving configuration:', error);
    }
  };

  const renderTabContent = () => {
    const baseTab = activeTab.split('/')[0];
    switch (baseTab) {
      case 'Chatbot':
        return {
          title: 'Welcome to Chatbot Builder! 👋',
          subtitle: 'Your dashboard is all set! Start managing your configurations or export your chatbot.',
          component: (
            <Chatbot
              selectedChatbotId={selectedChatbotId}
              setSelectedChatbotId={setSelectedChatbotIdProxy}
              activeSubTab={activeSubTab}
              setActiveSubTab={setActiveSubTabProxy}
            />
          ),
        };
      case 'Inbox':
        return {
          title: 'Unified Inbox',
          subtitle: 'Manage all customer conversations in one place.',
          component: <Inbox selectedChatbotId={selectedChatbotId} targetThreadId={targetThreadId} />
        };
      case 'Analytics':
        return {
          title: 'Unified Analytics',
          subtitle: 'Comprehensive performance and engagement insights across all chatbots.',
          component: <Dashboard selectedChatbotId={selectedChatbotId || 'all'} />
        };
      case 'Voicebot':
        return {
          title: 'AI Voicebot Builder',
          component: (
            <FeatureGuard feature={FEATURES.VOICEBOT} requiredPlan="pro_plus">
              <Voicebot />
            </FeatureGuard>
          ),
        };
      case 'Pro+':
        return {
          title: 'Pro+ Settings',
          component: (
            <FeatureGuard feature={FEATURES.PRO_SETTINGS} requiredPlan="pro_plus">
              <ProSettings
                chatbotRolePrompt={configState.chatbotRolePrompt}
                setChatbotRolePrompt={(value) => updateConfig('chatbotRolePrompt', value)}
                emailChatTranscriptsTo={configState.emailChatTranscriptsTo}
                setEmailChatTranscriptsTo={(value) => updateConfig('emailChatTranscriptsTo', value)}
              />
            </FeatureGuard>
          ),
        };
      case 'AdminImpersonation':
        return {
          title: 'Admin Impersonation',
          subtitle: 'Admin-only user impersonation with audited, read-only sessions.',
          component: <ImpersonationPage />,
        };
      default:
        return { title: '', component: null };
    }
  };

  // Ensure user has access to the current tab, otherwise redirect to accessible tab
  useEffect(() => {
    const tabFeatures = {
      'Chatbot': FEATURES.CHATBOT_BUILDER,
      'Voicebot': FEATURES.VOICEBOT,
      'Pro+': FEATURES.PRO_SETTINGS,
    };

    const baseTab = activeTab.split('/')[0];
    const currentTabFeature = tabFeatures[baseTab];
    if (currentTabFeature && !hasFeatureAccess(currentTabFeature)) {
      // Redirect to the first accessible tab
      const accessibleTabs = Object.keys(tabFeatures).filter(tab =>
        hasFeatureAccess(tabFeatures[tab])
      );
      if (accessibleTabs.length > 0) {
        handleTabChange(accessibleTabs[0]);
      }
    }
  }, [activeTab, hasFeatureAccess, handleTabChange, FEATURES]);

  if (loading) {
    return (
      <div className="h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-slate-500 font-medium">Loading workspace...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      <Toaster />
      <GlobalHandoffListener onNotificationClick={handleNotificationClick} />
      {/* Sidebar is fixed, so we add a margin to the main content */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        selectedChatbotId={selectedChatbotId}
        activeSubTab={activeSubTab}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={handleMobileMenuToggle}
        chatbots={chatbots}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      {/* Main Content Area */}
      <div className={`flex-1 h-full flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        {/* Mobile Header Trigger (Only visible on mobile) */}
        <div className="md:hidden p-4 border-b border-white bg-white/80 backdrop-blur sticky top-0 z-20 flex justify-between items-center shrink-0">
          <span className="font-bold text-slate-800">SmushLabs</span>
          <button onClick={() => setIsMobileMenuOpen(true)}>
            <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-hidden relative">
          <Content
            activeTabContent={renderTabContent()}
            resetConfigurations={resetToDefaultConfig}
            isMobileMenuOpen={isMobileMenuOpen}
            onMobileMenuToggle={handleMobileMenuToggle}
          />
        </div>
      </div>
    </div>
  );
}

export default Platform;
